import { Link, useLocation } from 'react-router-dom';
import { Car, Building2, Newspaper, Menu, X, LogIn, LogOut, Tag, Wallet, GitCompareArrows, ShieldCheck, Store, PlusCircle, User, Sparkles, Star, Users, Mail, HelpCircle, Wrench, BookOpen, MessageSquare, CarFront, Share2, Facebook, Instagram, Youtube, Heart } from 'lucide-react';

import { NewsletterSignup } from '@/components/newsletter/NewsletterSignup';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useCompare } from '@/contexts/CompareContext';
import { NewsStrip } from './NewsStrip';
import { FloatingActions } from '@/components/FloatingActions';
import { FooterShareBlock } from '@/components/layout/FooterShareBlock';
import { FooterFollowBlock } from '@/components/layout/FooterFollowBlock';
import { FooterLatestNews } from '@/components/layout/FooterLatestNews';
import { TopAffiliateStrip } from '@/components/layout/TopAffiliateStrip';
import { MobileTabBar } from '@/components/layout/MobileTabBar';
import { buildSocialUrl } from '@/lib/social-links';


import { Badge } from '@/components/ui/badge';

import { openCookieSettings } from '@/components/consent/CookieBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function PublicLayout({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();
  const { selectedCars } = useCompare();

  // Lock body scroll when mobile menu is open
  if (typeof document !== 'undefined') {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
  }

  const primaryNavLinks = [
    { href: '/biler', label: 'Find elbil', icon: Car },
    { href: '/find-bil', label: 'Elbilhjælp', icon: Sparkles },
    { href: '/nyheder', label: 'Nyheder', icon: Newspaper },
    { href: '/magasin', label: 'Magasin', icon: BookOpen },
    { href: '/vaerktoejer', label: 'Partnere', icon: Wrench },

    { href: '/community', label: 'Fællesskab', icon: Users },
    { href: '/kampagner', label: 'Kampagner', icon: Tag },
    { href: '/maerker', label: 'Mærker', icon: Building2 },
    { href: '/sammenlign', label: 'Sammenlign', icon: GitCompareArrows, showBadge: true },
    { href: '/okonomi', label: 'Økonomi', icon: Wallet },
  ];

  const secondaryNavLinks = [
    { href: '/guides', label: 'Guides', icon: BookOpen },
    { href: '/anmeldelser', label: 'Anmeldelser', icon: Star },
    { href: '/bidrag', label: 'Databidrag', icon: PlusCircle },
    { href: '/faq', label: 'FAQ', icon: HelpCircle },
  ];

  const allNavLinks = [...primaryNavLinks, ...secondaryNavLinks];

  const isActive = (href: string) => location.pathname.startsWith(href);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* News Strip */}
      <div className="hidden lg:block">
        <NewsStrip />
      </div>





      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between gap-4 h-16">
            {/* Left side: logo + mobile social links */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity flex-shrink-0" aria-label="Min Nye Elbil – forside">
                <img src="/logo.svg" alt="" width={40} height={40} decoding="async" {...({ fetchpriority: "high" } as any)} className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0" />
                <span className="hidden xl:inline-flex items-baseline gap-1 font-display font-extrabold tracking-tight text-base xl:text-lg leading-none whitespace-nowrap text-primary">
                  Min Nye<span className="text-accent">Elbil</span>
                </span>
              </Link>

              {/* Mobile social links */}
              <div className="lg:hidden flex items-center gap-1">
                <a
                  href={buildSocialUrl('facebook', 'mobile-header')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg Min Nye Elbil på Facebook"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={buildSocialUrl('instagram', 'mobile-header')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg Min Nye Elbil på Instagram"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={buildSocialUrl('youtube', 'mobile-header')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg Min Nye Elbil på YouTube"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
            <nav className="hidden lg:flex items-center gap-1">
              {primaryNavLinks.map(link => (
                <Link key={link.href} to={link.href}>
                  <Button
                    variant={isActive(link.href) ? 'navActive' : 'ghost'}
                    className="gap-2"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                    {link.showBadge && selectedCars.length > 0 && (
                      <Badge variant="default" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                        {selectedCars.length}
                      </Badge>
                    )}
                  </Button>
                </Link>
              ))}
              {/* "Mere" dropdown for secondary links */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <Menu className="w-4 h-4" />
                    Mere
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {secondaryNavLinks.map(link => (
                    <DropdownMenuItem key={link.href} asChild>
                      <Link to={link.href} className="flex items-center gap-2">
                        <link.icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Right side actions */}
            <div className="hidden lg:flex items-center gap-2">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      <User className="w-4 h-4" />
                      Min konto
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to="/community/profil/mig" className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Min profil & garage
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/mine-biler" className="flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        Mine biler
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/community" className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Fællesskab
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/bidrag" className="flex items-center gap-2">
                        <PlusCircle className="w-4 h-4" />
                        Bidrag med data
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" />
                            Admin
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => signOut()} className="flex items-center gap-2 cursor-pointer">
                      <LogOut className="w-4 h-4" />
                      Log ud
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      <LogIn className="w-4 h-4" />
                      Log ind
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/forhandler" className="flex items-center gap-2">
                        <Store className="w-4 h-4" />
                        Forhandler
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/login?redirect=/admin" className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        Administrator
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Mobile actions: compare badge + menu button */}
            <div className="lg:hidden flex items-center gap-1">
              {selectedCars.length > 0 && (
                <Link
                  to="/sammenlign"
                  className="relative inline-flex items-center justify-center min-w-11 min-h-11 rounded-lg hover:bg-secondary"
                  aria-label={`Sammenlign ${selectedCars.length} elbiler`}
                >
                  <GitCompareArrows className="w-5 h-5" />
                  <Badge variant="default" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1.5 text-xs">
                    {selectedCars.length}
                  </Badge>
                </Link>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center min-w-11 min-h-11 rounded-lg hover:bg-secondary border border-transparent hover:border-border transition-colors"
                aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu overlay backdrop */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 top-16 bg-black/40 z-40 animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-border bg-card animate-fade-in max-h-[72vh] overflow-y-auto relative z-50 pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
            <div className="container mx-auto px-4 py-3 space-y-1">
              {/* Primary links */}
              <p className="text-xs font-medium text-muted-foreground px-4 pt-1 pb-1">Hovedmenu</p>
              {primaryNavLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                    isActive(link.href)
                      ? 'bg-accent/10 text-accent'
                      : 'hover:bg-secondary'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                  {link.showBadge && selectedCars.length > 0 && (
                    <Badge variant="default" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                      {selectedCars.length}
                    </Badge>
                  )}
                </Link>
              ))}
              {/* Secondary links */}
              <div className="border-t border-border my-1" />
              <p className="text-xs font-medium text-muted-foreground px-4 pt-1 pb-1">Mere</p>
              {secondaryNavLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                    isActive(link.href)
                      ? 'bg-accent/10 text-accent'
                      : 'hover:bg-secondary'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}

              <div className="pt-2 border-t border-border">
                {user ? (
                  <div className="space-y-2">
                    <Link to="/community/profil/mig" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full gap-2 justify-start">
                        <User className="w-4 h-4" />
                        Min profil & garage
                      </Button>
                    </Link>
                    <Link to="/mine-biler" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full gap-2 justify-start">
                        <Heart className="w-4 h-4" />
                        Mine biler
                      </Button>
                    </Link>

                    {isAdmin && (
                      <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="accent" className="w-full gap-2 justify-start">
                          <ShieldCheck className="w-4 h-4" />
                          Admin Dashboard
                        </Button>
                      </Link>
                    )}
                    <Button
                      variant="ghost"
                      className="w-full gap-2 justify-start"
                      onClick={() => { setMobileMenuOpen(false); signOut(); }}
                    >
                      <LogOut className="w-4 h-4" />
                      Log ud
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link to="/forhandler" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full gap-2">
                        <Store className="w-4 h-4" />
                        Forhandler-login
                      </Button>
                    </Link>
                    <Link to="/login?redirect=/admin" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        Admin-login
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              {/* Handlinger — erstatter de flydende knapper på mobil */}
              <div className="border-t border-border mt-2 pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground px-1 pb-1">Hjælp os med at blive bedre</p>
                <Button
                  variant="accent"
                  className="w-full gap-2 justify-start"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.dispatchEvent(new CustomEvent('mne:open-feedback'));
                  }}
                >
                  <MessageSquare className="w-4 h-4" />
                  Giv feedback
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-2 justify-start"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.dispatchEvent(new CustomEvent('mne:open-suggest'));
                  }}
                >
                  <CarFront className="w-4 h-4" />
                  Mangler vi en elbil?
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-2 justify-start"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.dispatchEvent(new CustomEvent('mne:share-page'));
                  }}
                >
                  <Share2 className="w-4 h-4" />
                  Del denne side
                </Button>
              </div>
            </div>

          </nav>
        )}
      </header>

      {/* Annoncebånd — kun desktop. På mobil vises annoncen inde i indholdet. */}
      <div className="hidden lg:block">
        <TopAffiliateStrip />
      </div>

      {/* Floating Buttons (desktop) */}
      <FloatingActions />

      {/* Main content */}
      <main className="flex-1 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-0">{children}</main>


      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-8">
            <div className="col-span-2 md:col-span-3 lg:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo.svg" alt="" width={36} height={36} loading="lazy" decoding="async" className="w-9 h-9 flex-shrink-0" />
                <span className="font-display font-extrabold text-lg tracking-tight">
                  Min Nye<span className="text-accent"> Elbil</span>
                </span>
              </div>
              <p className="text-primary-foreground/70 text-sm">
                Vi samler alle nye elbiler fra bilmærkernes hjemmesider, danske tests og nyheder ét sted.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4">Navigation</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li><Link to="/biler" className="hover:text-accent transition-colors">Søg elbiler</Link></li>
                <li><Link to="/sammenlign" className="hover:text-accent transition-colors">Sammenlign elbiler</Link></li>
                <li><Link to="/maerker" className="hover:text-accent transition-colors">Alle mærker</Link></li>
                <li><Link to="/kampagner" className="hover:text-accent transition-colors">Kampagner</Link></li>
                <li><Link to="/nyheder" className="hover:text-accent transition-colors">Nyheder</Link></li>
                <li><Link to="/magasin" className="hover:text-accent transition-colors">Månedsmagasin</Link></li>

                <li><Link to="/anmeldelser" className="hover:text-accent transition-colors">Anmeldelser</Link></li>
                <li><Link to="/okonomi" className="hover:text-accent transition-colors">Økonomi</Link></li>
                <li><Link to="/vaerktoejer" className="hover:text-accent transition-colors">Partnere</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4">Find din elbil</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li><Link to="/find-bil" className="hover:text-accent transition-colors">Elbilhjælp</Link></li>
                <li><Link to="/biler?minRange=500" className="hover:text-accent transition-colors">Lang rækkevidde</Link></li>
                <li><Link to="/biler?bodyType=SUV" className="hover:text-accent transition-colors">SUV'er</Link></li>
                <li><Link to="/biler?bodyType=Station+Wagon" className="hover:text-accent transition-colors">Stationcars</Link></li>
                <li><Link to="/guides/elbiler-under-300000-kr" className="hover:text-accent transition-colors">Under 300.000 kr.</Link></li>
                <li><Link to="/guides/elbiler-med-7-saeder" className="hover:text-accent transition-colors">7 sæder</Link></li>
                <li><Link to="/guides" className="hover:text-accent transition-colors font-medium">Alle guides →</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4">Populære mærker</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/70">
                <li><Link to="/maerker/mercedes-benz" className="hover:text-accent transition-colors">Mercedes-Benz</Link></li>
                <li><Link to="/maerker/volkswagen" className="hover:text-accent transition-colors">Volkswagen</Link></li>
                <li><Link to="/maerker/bmw" className="hover:text-accent transition-colors">BMW</Link></li>
                <li><Link to="/maerker/byd" className="hover:text-accent transition-colors">BYD</Link></li>
                <li><Link to="/maerker/kia" className="hover:text-accent transition-colors">Kia</Link></li>
                <li><Link to="/maerker/volvo" className="hover:text-accent transition-colors">Volvo</Link></li>
                <li><Link to="/maerker/hyundai" className="hover:text-accent transition-colors">Hyundai</Link></li>
                <li><Link to="/maerker/tesla" className="hover:text-accent transition-colors">Tesla</Link></li>
                <li><Link to="/maerker" className="hover:text-accent transition-colors font-medium">Se alle mærker →</Link></li>
              </ul>
            </div>
            <FooterLatestNews />
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4">Om os</h4>
              <p className="text-sm text-primary-foreground/70 mb-3">
                Min Nye Elbil er en database med oplysninger om elbiler der kan købes i Danmark.
              </p>
              <Link to="/om-min-nye-elbil" className="text-sm text-accent hover:underline inline-flex items-center gap-1 mb-2">
                Om Min Nye Elbil →
              </Link>
              <br />
              <Link to="/nyhedsbrev" className="text-sm text-accent hover:underline inline-flex items-center gap-1 mt-2">
                <Mail className="w-3 h-3" /> Tilmeld nyhedsbrev
              </Link>
          </div>

          </div>

          <FooterFollowBlock />



          <FooterShareBlock />

          <div className="border-t border-primary-foreground/10 mt-8 pt-8">

            <div className="max-w-md mx-auto">
              <h4 className="font-semibold mb-2 text-center flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 text-accent" /> Min Nye Elbil nyhedsbrev
              </h4>
              <NewsletterSignup />
            </div>
          </div>
          <div className="border-t border-primary-foreground/10 mt-6 sm:mt-8 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-primary-foreground/50">
            <div className="flex items-center gap-3 text-center md:text-left">
              <span>© {new Date().getFullYear()} Min Nye Elbil</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link to="/persondatapolitik" className="hover:text-primary-foreground/70 transition-colors">
                Persondatapolitik
              </Link>
              <Link to="/vilkar" className="hover:text-primary-foreground/70 transition-colors">
                Vilkår
              </Link>
              <Link to="/cookies" className="hover:text-primary-foreground/70 transition-colors">
                Cookies
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                className="hover:text-primary-foreground/70 transition-colors"
              >
                Cookieindstillinger
              </button>
              <Link to="/login?redirect=/admin" className="hover:text-primary-foreground/70 transition-colors">
                Administration
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Fast bundnavigation på mobil */}
      <MobileTabBar moreOpen={mobileMenuOpen} onToggleMore={() => setMobileMenuOpen(!mobileMenuOpen)} />
    </div>
  );
}
